import { useEffect, useState, type FormEvent } from 'react'
import { adminApi, ApiError, type AdminOrganization, type AdminUser,
         type AdminRole, type AdminConnection } from '../lib/api'

const BLANK = {
  login: '', display_name: '', password: '', role_id: '',
  dispatch_connection_id: '',
}

/** A password you can hand over without it having travelled anywhere.
 *
 *  Generated in the browser from crypto.getRandomValues, not Math.random,
 *  which is not seeded for this and is predictable enough to matter. It is
 *  shown once so it can be copied, and then only its hash exists. */
function generatePassword(): string {
  const alphabet = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const bytes = new Uint32Array(20)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

/** Only dispatchers log in. Members never do — they use the radio card and a
 *  separate app — so there is exactly one kind of human user here, and no
 *  sign-up screen anywhere. Accounts are made for people. */
export function Users({ org }: { org: AdminOrganization }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [form, setForm] = useState({ ...BLANK })
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetting, setResetting] = useState<string | null>(null)
  const [seats, setSeats] = useState<AdminConnection[]>([])
  const [gpsBusy, setGpsBusy] = useState<string | null>(null)
  // Shown ONCE, and never fetched again. The server does not store it.
  const [gpsResult, setGpsResult] = useState<
    { userId: string; email?: string; password: string | null; detail: string } | null
  >(null)

  // A revoke whose remote half did not happen comes back as `warning` on the
  // PATCH. It was being thrown away, which broke the promise access.py makes
  // in its own docstring: "What the operator must never get is a silent
  // failure." The CRM would say revoked while the GPS login stayed live.
  async function setGps(u: AdminUser, granted: boolean) {
    setGpsBusy(u.id)
    setError(null)
    try {
      const out = await adminApi.userGpsAccess(org.id, u.id, granted)
      setGpsResult({
        userId: u.id, email: out.email, password: out.password,
        detail: out.detail,
      })
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change GPS access.')
    } finally {
      setGpsBusy(null)
    }
  }

  async function load() {
    try {
      const res = await adminApi.users(org.id)
      setUsers(res.users)
      setRoles(res.roles)
      const conns = await adminApi.dispatchConnections(org.id)
      setSeats(conns.connections)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load logins.')
    }
  }

  useEffect(() => {
    load()
    setAdding(false)
    setResetting(null)
    setForm({ ...BLANK })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id])

  async function add(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await adminApi.userCreate(org.id, {
        ...form,
        role_id: form.role_id || null,
        dispatch_connection_id: form.dispatch_connection_id || null,
      })
      setForm({ ...BLANK })
      setAdding(false)
      await load()
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add that login.')
    } finally {
      setBusy(false)
    }
  }

  async function patch(u: AdminUser, body: Record<string, unknown>) {
    try {
      const out = await adminApi.userUpdate(org.id, u.id, body)
      // Disabling somebody also revokes their GPS login, and that half can
      // fail on its own — the platform being unreachable, say. The server
      // reports it as `warning` rather than failing the request, because the
      // CRM lock-out must happen regardless. Showing it is the other half of
      // that promise: without this the operator is told nothing and a live GPS
      // login stays live.
      if (out.warning) setError(out.warning)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update.')
    }
  }

  return (
    <div className="pad">
      <h2>Logins for {org.name}</h2>
      <p className="blurb">
        Dispatchers and their org admin. An org admin is simply a dispatcher
        whose role has the Settings branch unlocked — no separate build, and
        they can dispatch when short-handed.
      </p>

      {error && <div className="notice">{error}</div>}

      {users.length === 0 ? (
        <div className="listempty">No logins yet.</div>
      ) : (
        users.map((u) => (
          <div key={u.id}>
            <div className={`listrow ${u.disabled ? 'retired' : ''}`}>
              <span className="nm">{u.display_name}</span>
              <span className="mono chans">{u.login}</span>
              <span className="tag">{u.role_name ?? 'no role'}</span>
              <select
                className="seatpick"
                value={u.dispatch_connection_id ?? ''}
                onChange={(e) =>
                  patch(u, { dispatch_connection_id: e.target.value || null })
                }
              >
                <option value="">No connection</option>
                {seats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                    {c.fleet_account_ready ? '' : '  (not ready)'}
                  </option>
                ))}
              </select>
              {u.disabled && <span className="pill">disabled</span>}
              {u.traccar_access && <span className="pill go">GPS</span>}
              {/* One button, three states. Granting creates their Traccar
                  login through the organization's own manager account;
                  revoking DISABLES it there rather than deleting it, so the
                  account and its history survive and access can be given back
                  without making a second login for the same person.

                  Disabling the CRM user revokes it automatically — that is the
                  whole reason the CRM owns this link, rather than somebody
                  remembering to tidy up in Traccar after a leaver. */}
              <button
                className="ghost tiny nogrow"
                disabled={gpsBusy === u.id || (u.disabled && !u.traccar_access)}
                title={u.disabled && !u.traccar_access
                  ? 'Re-enable this login first'
                  : u.traccar_access
                    ? 'Disable their GPS platform login'
                    : 'Create a GPS platform login for them'}
                onClick={() => setGps(u, !u.traccar_access)}
              >
                {gpsBusy === u.id
                  ? 'Working…'
                  : u.traccar_access ? 'Revoke GPS' : 'Grant GPS'}
              </button>
              <button
                className="ghost tiny"
                onClick={() => setResetting(resetting === u.id ? null : u.id)}
              >
                {resetting === u.id ? 'Cancel' : 'Set password'}
              </button>
              <button
                className="ghost tiny nogrow"
                onClick={() => patch(u, { disabled: !u.disabled })}
              >
                {u.disabled ? 'Enable' : 'Disable'}
              </button>
            </div>


            {/* The password, once. There is no second chance to read it and
                the panel says so, because the alternative is an operator
                closing it and quietly having no way to hand the login over. */}
            {gpsResult?.userId === u.id && (
              <div className="notice gpsgrant">
                <p>{gpsResult.detail}</p>
                {gpsResult.password && (
                  <p className="mono">
                    {gpsResult.email} &nbsp; {gpsResult.password}
                  </p>
                )}
                <button className="ghost tiny" onClick={() => setGpsResult(null)}>
                  Done
                </button>
              </div>
            )}

            {resetting === u.id && (
              <SetPassword
                user={u}
                onCancel={() => setResetting(null)}
                onSave={async (password) => {
                  await patch(u, { password })
                  setResetting(null)
                }}
              />
            )}
          </div>
        ))
      )}

      <p className="footnote">
        The connection is the seat this dispatcher takes when they sign in, and
        its name is what the field hears — “Dispatcher 1”, not their own name.
        Several volunteers can be assigned the same connection; only one sits
        at it at a time. Connections are created on the Dispatch tab.
      </p>

      {adding ? (
        <form onSubmit={add} className="addform">
          <h3 className="sub">New login</h3>
          <div className="grid2">
            <div className="field">
              <label>Login</label>
              <input value={form.login} required
                     onChange={(e) => setForm({ ...form, login: e.target.value })} />
            </div>
            <div className="field">
              <label>Their name</label>
              <input value={form.display_name} required
                     onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            </div>
            <div className="field">
              <label>Password — 12 characters or more</label>
              <div className="inline">
                <input type="text" className="mono" value={form.password} required
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <button type="button" className="ghost"
                        onClick={() => setForm({ ...form, password: generatePassword() })}>
                  Generate
                </button>
              </div>
            </div>
            <div className="field">
              <label>Role</label>
              <select className="pick" value={form.role_id}
                      onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
                <option value="">Least powerful</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} — panel level {r.dispatch_level}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Dispatch connection — what the field hears</label>
              <select
                className="pick"
                value={form.dispatch_connection_id}
                onChange={(e) =>
                  setForm({ ...form, dispatch_connection_id: e.target.value })
                }
              >
                <option value="">No connection — cannot go on air</option>
                {seats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                    {c.fleet_account_ready ? '' : '  (not ready)'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="footnote">
            The panel level is a ceiling, not a grant: the CRM can never hand
            out a dispatch feature the level forbids. Level 1 carries covert
            listen-in, so give it deliberately rather than by default.
          </p>
          <p className="footnote">
            The connection is the seat they take when they sign in, and its
            name is what goes out on the air. Several dispatchers can share one
            — only one sits at it at a time. Leave it blank and they can still
            work every other part of the CRM; they just cannot go on air.
          </p>
          <div className="btnrow">
            <button className="primary compact" disabled={busy}>
              {busy ? 'Adding…' : 'Add login'}
            </button>
            <button type="button" className="ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="btnrow">
          <button className="primary compact" onClick={() => setAdding(true)}>
            Add a login
          </button>
        </div>
      )}
    </div>
  )
}

function SetPassword({
  user,
  onSave,
  onCancel,
}: {
  user: AdminUser
  onSave: (password: string) => Promise<void>
  onCancel: () => void
}) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const tooShort = password.length > 0 && password.length < 12

  if (done) {
    return (
      <div className="resetbox">
        <div className="testresult ok">
          Password set for <strong>{user.login}</strong>. Hand it over now — only
          its hash is stored, so it cannot be read back from here or anywhere else.
        </div>
        <div className="btnrow">
          <button className="ghost" onClick={onCancel}>Done</button>
        </div>
      </div>
    )
  }

  return (
    <div className="resetbox">
      <div className="field">
        <label>New password for {user.login} — 12 characters or more</label>
        {/* Shown as text, not dots. Nobody can hand over a password they
            cannot read, and this is being set FOR somebody else rather than
            typed by its owner. */}
        <div className="inline">
          <input
            className="mono"
            type="text"
            value={password}
            autoFocus
            placeholder="type one, or generate"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button className="ghost" onClick={() => setPassword(generatePassword())}>
            Generate
          </button>
        </div>
        {tooShort && <p className="footnote warn">At least 12 characters.</p>}
      </div>

      <div className="btnrow">
        <button
          className="primary compact"
          disabled={busy || password.length < 12}
          onClick={async () => {
            setBusy(true)
            try {
              await onSave(password)
              setDone(true)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Setting…' : 'Set password'}
        </button>
        <button className="ghost" onClick={onCancel}>Cancel</button>
      </div>

      <p className="footnote">
        This replaces the password immediately. It does not sign them out of a
        session they already hold — use Disable for that.
      </p>
    </div>
  )
}
